import React, { useState, useMemo } from 'react';
import Card from '../../../Components/Ui/Card';
import Pagination from '../../../Components/Ui/Pagination';
import { toast } from 'react-toastify';
import Button from '../../../Components/Ui/Button';
import BrandTable from './BrandTable';
import Brandfilter from './Brandfilter';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import {
  useGetSellerBrandProductsQuery,
  useGetAllCategoriesQuery,
  useGetSellerBrandsQuery,
} from '../../../app/services/productApi';
import { useGetSellerCatalogQuery } from '../../../app/services/catalogApi';
import { useSelector } from 'react-redux';
import Loader from '../../../Components/Ui/Loader';

const BrandCatalog = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.userInfo);
  const { brandId: ownedBrandId } = useSelector((state) => state.myBrand);
  const { _id: sellerId, userId } = user;

  const { data: sellerCatalog } = useGetSellerCatalogQuery({
    sellerId: sellerId || userId,
    limit: 10, // Fetch all items
  });

  const { data, isLoading, isError, isFetching, refetch } = useGetSellerBrandProductsQuery(
    {
      sellerId: sellerId || userId,
      search: searchQuery,
      page: currentPage,
      limit: itemsPerPage,
      category: categoryFilter || undefined,
      brand: brandFilter || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: { data: categoriesData } = { data: [] } } = useGetAllCategoriesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: { data: brandsData } = { data: [] } } = useGetSellerBrandsQuery(
    sellerId || userId,
    {
      refetchOnMountOrArgChange: true,
      skip: !(sellerId || userId),
    },
  );

  const sellerCatalogIds = useMemo(() => {
    return new Set(sellerCatalog?.data?.map((item) => item.productId) || []);
  }, [sellerCatalog]);

  const products = useMemo(() => {
    return data?.data?.filter((product) => !sellerCatalogIds.has(product._id)) || [];
  }, [data, sellerCatalogIds]);

  const categories = categoriesData || [];
  const brands = brandsData || [];
  const totalProducts = data?.pagination?.totalItems || 0;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);

  const handleAddToCatalog = () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one product');
      return;
    }
    if (selectedItems.length > 1) {
      toast.warning('Please select only one product at a time.');
      return;
    }

    const selectedProductId = selectedItems[0];
    const product = products.find((p) => p._id === selectedProductId);

    if (product) {
      const productBrandId = product.brand?._id || product.brand;

      // Bypass if it's the seller's own brand
      const isOwnBrand = ownedBrandId && productBrandId === ownedBrandId;

      const brandDeal = user?.brandDeal?.find(
        (deal) => (deal.brand?._id || deal.brand) === productBrandId,
      );

      if (!isOwnBrand && brandDeal?.status !== 'approved') {
        toast.error(
          `You are not approved to sell products for brand: ${
            product.brand?.name || 'this brand'
          }. Please wait for admin approval.`,
        );
        return;
      }
    }

    // toast.success('Product added to catalog');
    navigate(`/seller/catalogs/new/base/${selectedProductId}`, {
      state: { selectedProductId },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">Failed to load products</p>
        <Button onClick={refetch} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 tertiary-text-color">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 mb-4 sm:mb-0 pl-6">
          <h2 className="text-lg xl:text-2xl font-bold text-center md:text-left">Brand Catalog</h2>
          <p>You can choose only one product at a time to add to your catalog.</p>
        </div>
        <div className="flex gap-3">
          {selectedItems.length > 0 && (
            <Button
              onClick={handleAddToCatalog}
              className="bg-primary white-text px-4 py-2 rounded-md shadow hover:bg-primary-dark"
              disabled={isFetching}
            >
              Add to Catalog
              {/* ({selectedItems.length}) */}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <Brandfilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
          brandFilter={brandFilter}
          setBrandFilter={setBrandFilter}
          brands={brands}
          isLoading={isFetching}
          user={user} // Pass the user object
        />

        {products.length === 0 ? (
          <div className="text-center py-8">
            <p className="tertiary-text-color mb-4">No products found</p>
          </div>
        ) : (
          <>
            <BrandTable
              products={products}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              isLoading={isFetching}
            />

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm tertiary-text-color text-center sm:text-left">
                Showing {products.length} of {totalProducts} products
              </p>

              <div className="flex justify-center sm:justify-end overflow-x-auto">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={isFetching}
                />
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default BrandCatalog;
